import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function Pricing() {
  const plans = [
    {
      name: 'Starter',
      price: '$99',
      period: '/month',
      description: 'Perfect for small businesses getting started',
      features: [
        '5,000 customer notifications/month',
        '10,000 transactional emails/month',
        'Basic analytics',
        'Email support',
        'A2P 10DLC registration included',
        'Standard delivery speed'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Professional',
      price: '$299',
      period: '/month',
      description: 'Ideal for growing businesses',
      features: [
        '25,000 customer notifications/month',
        '50,000 transactional emails/month',
        'Advanced analytics',
        'Priority support',
        'A2P 10DLC registration included',
        'API access',
        'Custom templates',
        'Dedicated account manager'
      ],
      cta: 'Get Started',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large-scale operations',
      features: [
        'Unlimited customer notifications',
        'Unlimited transactional emails',
        'Custom analytics',
        '24/7 phone support',
        'Multiple A2P messaging campaigns',
        'Full API access',
        'Custom integrations',
        'Dedicated success team',
        'SLA guarantee'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  const addons = [
    { name: 'Additional Notifications (per 1,000)', price: '$15' },
    { name: 'Additional Emails (per 10,000)', price: '$10' },
    { name: 'Extra A2P Messaging Campaign', price: '$50/month' },
    { name: 'Premium Support', price: '$199/month' },
    { name: 'Custom Integration', price: 'Quote' }
  ];

  return (
    <>
      <Helmet>
        <title>Pricing - RMQ Digital</title>
        <meta name="description" content="Transparent pricing for customer communication platform. Plans starting at $99/month with A2P 10DLC registration included." />
      </Helmet>

      <div style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)', color: 'white', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}>Platform Pricing</h1>
          <p style={{ fontSize: '1.25rem', opacity: 0.95 }}>Choose the plan that fits your business communication needs</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        {/* Pricing Hero Image */}
        <div style={{ marginBottom: '4rem', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <img 
            src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=400&fit=crop" 
            alt="Business growth and pricing" 
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
          {plans.map((plan, idx) => (
            <div key={idx} style={{
              background: 'white',
              border: plan.popular ? '2px solid #6d28d9' : '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '2rem',
              position: 'relative',
              boxShadow: plan.popular ? '0 12px 24px rgba(109, 40, 217, 0.15)' : '0 4px 6px rgba(0, 0, 0, 0.05)',
              transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.3s ease'
            }}>
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#6d28d9',
                  color: 'white',
                  padding: '0.25rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}>
                  Most Popular
                </div>
              )}
              
              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
                {plan.name}
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem', minHeight: '3rem' }}>
                {plan.description}
              </p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '3rem', fontWeight: '800', color: '#6d28d9' }}>{plan.price}</span>
                <span style={{ fontSize: '1.1rem', color: '#6b7280' }}>{plan.period}</span>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                {plan.features.map((feature, fidx) => (
                  <li key={fidx} style={{
                    color: '#4b5563',
                    marginBottom: '0.75rem',
                    paddingLeft: '1.5rem',
                    position: 'relative',
                    lineHeight: '1.5'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: '#6d28d9',
                      fontWeight: '600'
                    }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <a href="/contact" style={{
                display: 'block',
                background: plan.popular ? 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' : '#f3f4f6',
                color: plan.popular ? 'white' : '#6d28d9',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}>
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <div style={{ background: '#f9fafb', padding: '3rem', borderRadius: '12px', marginBottom: '4rem' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem', textAlign: 'center' }}>
            Add-Ons & Extras
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {addons.map((addon, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #e5e7eb'
              }}>
                <span style={{ color: '#4b5563', fontWeight: '500' }}>{addon.name}</span>
                <span style={{ color: '#6d28d9', fontWeight: '700', fontSize: '1.1rem' }}>{addon.price}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
            Questions About Pricing?
          </h2>
          <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem' }}>
            Our team is here to help you choose the right plan
          </p>
          <a href="/contact" style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)',
            color: 'white',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem',
            transition: 'all 0.3s ease'
          }}>
            Contact Sales
          </a>
        </div>
      </div>
    </>
  );
}
